import React, { ReactNode, useMemo } from 'react'
import { Anchor, Form, Table } from 'react-bootstrap'

import _ from 'lodash'
import styled from 'styled-components'
import { StringParam, useQueryParams } from 'use-query-params'

import { Colours } from '@lib/theme'
import { DataRecords } from '@piv/lib/rest-queries'
import { Button } from '@piv/portal/primitives/Buttons'
import { DataFromDate } from '@piv/portal/primitives/DataFromDate'
import { useFullscreenSpinner } from '@piv/portal/primitives/FullscreenSpinner'
import { InlineError } from '@piv/portal/primitives/InlineError'

type Dto = DataRecords.Types.DataRecord
type FilterKey = DataRecords.Types.DataRecordFilterKey

type Column<T> = {
  key: string
  render: ((row: T) => React.ReactNode) | keyof T
  columnLabel: string
  filterKey?: FilterKey
  filterSpan?: number
  width: string
}

export interface Props {
  dataPackageId: string
  subAssetId: string
  onRowPreview: (row: Dto) => void
}

export function DrilldownTable({
  dataPackageId,
  subAssetId,
  onRowPreview,
}: Readonly<Props>) {
  const [filters, setFilters] = useQueryParams({
    record: StringParam,
    currency: StringParam,
    region: StringParam,
  } as Record<FilterKey, typeof StringParam>)

  const columns: Column<Dto>[] = useMemo(
    () => [
      {
        key: 'pad-left',
        columnLabel: '',
        width: '1rem',
        render: () => '',
      },
      {
        key: 'recordDescription',
        render: 'recordDescription',
        columnLabel: 'Record Description',
        filterKey: 'record',
        width: '30%',
      },
      {
        key: 'recordName',
        render: 'recordName',
        columnLabel: 'Record Name',
        width: '20%',
      },
      {
        key: 'Currency',
        render: (row) =>
          [row.currency1DisplayName, row.currency2DisplayName]
            .filter(Boolean)
            .join(', '),
        columnLabel: 'Currency',
        filterKey: 'currency',
        width: '7rem',
      },
      {
        key: 'regionDisplayName',
        render: 'regionDisplayName',
        columnLabel: 'Desk Region',
        filterKey: 'region',
        width: '7rem',
      },
      {
        key: 'dataSince',
        render: (row) => <DataFromDate date={row.dateFrom} />,
        columnLabel: 'Data back to',
        width: '7rem',
      },
      {
        key: 'controls',
        render: (row) => {
          return (
            <Anchor
              onClick={() => onRowPreview(row)}
              data-test-id="preview-link"
            >
              Preview
            </Anchor>
          )
        },
        columnLabel: '',
        width: 'auto',
      },
      {
        key: 'pad-right',
        columnLabel: '',
        width: '1rem',
        render: () => '',
      },
    ],
    [onRowPreview]
  )

  const debouncedSetFilters = useMemo(() => {
    return _(columns)
      .filter((col) => !!col.filterKey)
      .map<FilterKey>((col) => col.filterKey!)
      .keyBy((key) => key)
      .mapValues((key) => {
        return _.debounce(
          (e: any) =>
            setFilters({
              [key]: e.target.value,
            }),
          150,
          {
            leading: false,
            maxWait: 300,
            trailing: true,
          }
        )
      })
      .value()
  }, [columns, setFilters])

  const records = DataRecords.useDataRecords(dataPackageId, subAssetId, filters)

  useFullscreenSpinner(records.isPending)

  return (
    <StyledTable hover size="sm">
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              style={{
                width: col.width,
              }}
            >
              {col.columnLabel}
            </th>
          ))}
        </tr>
        <tr>
          {columns.map((col) => (
            <th key={col.key}>
              {!!col.filterKey && (
                <Form.Control
                  size="sm"
                  placeholder="Search"
                  name={col.filterKey}
                  defaultValue={filters[col.filterKey] || ''}
                  onChange={debouncedSetFilters[col.filterKey]}
                />
              )}
            </th>
          ))}
        </tr>
      </thead>

      <tbody id="record-data">
        {records.data?.pages
          .flatMap((page) => page.data)
          .map((row) => {
            return (
              <tr key={row.recordName} id={row.recordName}>
                {columns.map((col) => {
                  let display: ReactNode = ''

                  if (typeof col.render === 'function') {
                    display = col.render(row)
                  } else {
                    const value = row[col.render]
                    if (typeof value === 'string') {
                      display = value
                    } else {
                      console.warn(
                        '[DrilldownTable]',
                        col.render,
                        'could not be rendered because type of',
                        typeof row[col.render],
                        'is not supported'
                      )
                    }
                  }

                  return <td key={col.key}>{display}</td>
                })}
              </tr>
            )
          })}
      </tbody>

      <tfoot>
        <tr>
          <td colSpan={columns.length}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <InlineError isError={records.isError} />

              {records.hasNextPage && (
                <Button
                  variant="primary"
                  onClick={() => records.fetchNextPage()}
                  disabled={records.isFetching}
                >
                  Load More
                </Button>
              )}
            </div>
          </td>
        </tr>
      </tfoot>
    </StyledTable>
  )
}

const StyledTable = styled(Table)`
  width: 100%;

  table-layout: fixed;

  thead {
    position: sticky;
    top: 0;

    background-color: ${Colours.BgShade.Mid};

    -webkit-box-shadow: 2px 0px 15px 0px rgba(0, 0, 0, 0.75);
    -moz-box-shadow: 2px 0px 15px 0px rgba(0, 0, 0, 0.75);
    box-shadow: 2px 0px 15px 0px rgba(0, 0, 0, 0.75);

    th {
      border-bottom-width: 0 !important;

      padding-top: 0;
      padding-bottom: 0;
    }

    > :first-child > th {
      padding-top: 0.5rem;
      padding-bottom: 0.25rem;
    }
    > :last-child > th {
      padding-bottom: 0.5rem;
    }
    > input {
      max-width: 10rem;
    }
  }

  tbody {
    border-top: none !important;

    tr:first-child td {
      padding-top: 1rem;
    }

    td {
      overflow-wrap: break-word;
    }
  }

  tfoot {
    border-top: none !important;

    td {
      border-bottom: none;
      padding-top: 1rem;
    }
  }
`
